"use client"

import HomeDetailsWrapperAccordion from "@/features/homeowners/components/home-details-wrapper-accordion"
import { useWorkscopeLabelStyle, WORKSCOPE_LABEL_COLORS } from "../home-details-wrapper-accordion"
import { workScopeStatusOptions } from "../../constants/options"
import FormSelectInput from "../../../../shared/components/form-inputs/form-select-input"
import FormMultiSelectInput from "../../../../shared/components/form-inputs/form-multi-select-input"
import FormTextInput from "@/shared/components/form-inputs/form-text-input"
import FormTextareaInput from "../../../../shared/components/form-inputs/form-textarea-input"
import DateStampedTextarea from "../../../../shared/components/form-inputs/form-date-stamped-textarea-input"
import { HomeWithHomeOwnerFormType } from "../../schema/home-with-home-owner.schema"
import { useFormContext, useWatch } from "react-hook-form"
import { useClaimedByOptions } from "../../hooks/use-claimed-by-options"
import { useWorkscopeClaimedBySync } from "../../hooks/use-workscope-claimed-by-sync"
import { GoodwillToggleLabel } from "../goodwill-toggle-label"

const goodwillGreenStyle = { color: WORKSCOPE_LABEL_COLORS.goodwill } as const
const BASE_FIELD_NAME = "home.insulationInstallInfo"

function HomeInsulationInstallForm() {
    const goodwillLabelStyle = useWorkscopeLabelStyle(true) ?? goodwillGreenStyle
    const { control, setValue, getValues } = useFormContext<HomeWithHomeOwnerFormType>()
    const { options: claimedByOptions, loading: claimedByLoading } = useClaimedByOptions()
    const goodwillInsulationInstallOn = useWatch({ control, name: `${BASE_FIELD_NAME}.goodwillInsulationInstallOn` })
    
    // Watch status field and claimed by field
    const insulationInstall = useWatch({ control, name: `${BASE_FIELD_NAME}.insulationInstall` })
    const claimedBy = useWatch({ control, name: `${BASE_FIELD_NAME}.claimedBy` })
    const goodwillInsulationInstall = useWatch({ control, name: `${BASE_FIELD_NAME}.goodwillInsulationInstall` })
    const goodwillInsulationInstallClaimedBy = useWatch({
        control,
        name: `${BASE_FIELD_NAME}.goodwillInsulationInstallClaimedBy`,
    })

    useWorkscopeClaimedBySync<HomeWithHomeOwnerFormType>({
        status: insulationInstall,
        claimedBy,
        claimedByFieldPath: `${BASE_FIELD_NAME}.claimedBy`,
        setValue,
        getValues,
    })
    useWorkscopeClaimedBySync<HomeWithHomeOwnerFormType>({
        status: goodwillInsulationInstall,
        claimedBy: goodwillInsulationInstallClaimedBy,
        claimedByFieldPath: `${BASE_FIELD_NAME}.goodwillInsulationInstallClaimedBy`,
        setValue,
        getValues,
        enabled: !!goodwillInsulationInstallOn,
    })

    return (
        <HomeDetailsWrapperAccordion
            heading="Insulation Install"
            wrapperClassName="xl:grid-cols-6"
        >
            {/* Row 1: Insulation Install + Claimed By 50/50 (like Trim Install) */}
            <FormSelectInput
                inputGroupProps={{
                    label: <GoodwillToggleLabel title="Insulation Install" toggleName={`${BASE_FIELD_NAME}.goodwillInsulationInstallOn` as any} control={control} />,
                    wrapperClassName: "xl:col-span-3",
                }}
                options={workScopeStatusOptions}
                name={`${BASE_FIELD_NAME}.insulationInstall`}
                control={control}
            />
            <FormMultiSelectInput
                inputGroupProps={{
                    label: "Claimed By",
                    wrapperClassName: "xl:col-span-3",
                }}
                options={claimedByOptions}
                name={`${BASE_FIELD_NAME}.claimedBy`}
                control={control}
                placeholder={claimedByLoading ? "Loading options..." : "Select"}
                searchPlaceholder="Search options..."
            />
            {goodwillInsulationInstallOn && (
                <>
                    <FormSelectInput
                        inputGroupProps={{ label: "Insulation Install Goodwill", labelStyle: goodwillLabelStyle, wrapperClassName: "xl:col-span-3" }}
                        options={workScopeStatusOptions}
                        name={`${BASE_FIELD_NAME}.goodwillInsulationInstall`}
                        control={control}
                    />
                    <FormMultiSelectInput
                        inputGroupProps={{ label: "Insulation Install Goodwill Claimed By", wrapperClassName: "xl:col-span-3" }}
                        options={claimedByOptions}
                        name={`${BASE_FIELD_NAME}.goodwillInsulationInstallClaimedBy`}
                        control={control}
                        placeholder={claimedByLoading ? "Loading options..." : "Select"}
                        searchPlaceholder="Search options..."
                    />
                </>
            )}
            {/* Row 2: All square feet questions on one row */}
            <FormTextInput
                inputGroupProps={{
                    label: "Square feet of blanket wall insulation needed",
                    wrapperClassName: "xl:col-span-2",
                }}
                placeholder="Square feet of blanket wall insulation needed"
                name={`${BASE_FIELD_NAME}.blanketWallSquareFeet`}
                type="number"
                control={control}
            />
            <FormTextInput
                inputGroupProps={{
                    label: "Square feet of rigid wall insulation needed",
                    wrapperClassName: "xl:col-span-2",
                }}
                placeholder="Square feet of rigid wall insulation needed"
                name={`${BASE_FIELD_NAME}.rigidWallSquareFeet`}
                type="number"
                control={control}
            />
            <FormTextInput
                inputGroupProps={{
                    label: "Square feet of ceiling insulation needed",
                    wrapperClassName: "xl:col-span-2",
                }}
                placeholder="Square feet of ceiling insulation needed"
                name={`${BASE_FIELD_NAME}.ceilingInsulationSquareFeet`}
                type="number"
                control={control}
            />
            <DateStampedTextarea
                includeUserName
                inputGroupProps={{
                    label: "Give a brief description of the insulation install work needed",
                    wrapperClassName: "xl:col-span-6",
                }}
                rows={4}
                name={`${BASE_FIELD_NAME}.description`}
                control={control}
            />
        </HomeDetailsWrapperAccordion>
    )
}

export default HomeInsulationInstallForm
